import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { adminApi } from '../services/adminApi';
import { useAuth } from '../contexts/AuthContext';

interface AdminLoginForm {
  username: string;
  password: string;
}

const AdminLoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { checkAuth, isAuthenticated, isAdminAuthenticated, isInitialized } = useAuth();

  // Redirect authenticated users away from admin login page
  useEffect(() => {
    if (isInitialized) {
      if (isAdminAuthenticated) {
        navigate('/admin/dashboard', { replace: true });
      } else if (isAuthenticated) {
        navigate('/', { replace: true });
      }
    }
  }, [isInitialized, isAuthenticated, isAdminAuthenticated, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<AdminLoginForm>();

  // Don't show admin login page if user is authenticated
  if (!isInitialized || isAuthenticated || isAdminAuthenticated) {
    return null;
  }

  const onSubmit = async (data: AdminLoginForm) => {
    setIsLoading(true);
    try {
      const response = await adminApi.login(data);
      
      // Refresh authentication state in context (navbar will update immediately)
      await checkAuth();
      
      toast.success('خوش آمدید، ادمین!');
      navigate('/admin/dashboard'); // Redirect to admin dashboard
    } catch (error: any) {
      console.error('Admin login error:', error);
      toast.error(error.response?.data?.message || 'نام کاربری یا رمز عبور نامعتبر است');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8" dir="rtl">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            پنل مدیریت
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            برای دسترسی به پنل مدیریت وارد شوید
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                نام کاربری
              </label>
              <div className="mt-1">
                <input
                  {...register('username', {
                    required: 'نام کاربری الزامی است',
                    minLength: {
                      value: 3,
                      message: 'نام کاربری باید حداقل ۳ کاراکتر باشد'
                    }
                  })}
                  type="text"
                  autoComplete="username"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="نام کاربری خود را وارد کنید"
                />
                {errors.username && (
                  <p className="mt-2 text-sm text-red-600">{errors.username.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                رمز عبور
              </label>
              <div className="mt-1">
                <input
                  {...register('password', {
                    required: 'رمز عبور الزامی است',
                    minLength: {
                      value: 6,
                      message: 'رمز عبور باید حداقل ۶ کاراکتر باشد'
                    }
                  })}
                  type="password"
                  autoComplete="current-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="رمز عبور خود را وارد کنید"
                />
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -mr-1 ml-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    در حال ورود...
                  </>
                ) : (
                  'ورود'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">اعتبارات پیش‌فرض</span>
              </div>
            </div>

            <div className="mt-3 p-3 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-600 text-center">
               
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
          >
            بازگشت به سایت اصلی ←
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage; 
